import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { createUser, getUser, getProgress, updateProgress } from "../lib/api";
import { COURSE, getAllSubtopicIds } from "../data/course";
import { getLMSStudentName } from "../lib/scorm";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const LS_USER_KEY = "sap_elearn_user_id";
const LS_THEME_KEY = "sap_elearn_theme";

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState("light");

  // apply theme
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const registerUser = useCallback(async (name) => {
    const u = await createUser(name);
    setUser(u);
    localStorage.setItem(LS_USER_KEY, u.id);
    const p = await getProgress(u.id);
    setProgress(p);
    return u;
  }, []);

  // load existing user on mount
  useEffect(() => {
    (async () => {
      try {
        const lmsName = getLMSStudentName();
        const id = localStorage.getItem(LS_USER_KEY);

        if (id) {
          try {
            const [u, p] = await Promise.all([getUser(id), getProgress(id)]);
            if (lmsName && u.name !== lmsName) {
              localStorage.removeItem(LS_USER_KEY);
            } else {
              setUser(u);
              setProgress(p);
              setLoading(false);
              return;
            }
          } catch (e) {
            localStorage.removeItem(LS_USER_KEY);
          }
        }

        if (lmsName) {
          await registerUser(lmsName);
        }
      } catch (e) {
        localStorage.removeItem(LS_USER_KEY);
      } finally {
        setLoading(false);
      }
    })();
  }, [registerUser]);

  const persist = useCallback(
    async (patch) => {
      if (!user) return;
      setSaving(true);
      const optimistic = { ...progress, ...patch };
      setProgress(optimistic);
      try {
        const fresh = await updateProgress(user.id, patch);
        setProgress(fresh);
      } finally {
        setSaving(false);
      }
    },
    [user, progress]
  );

  // —————— actions ——————
  const toggleBookmark = useCallback(
    (subId) => {
      if (!progress) return;
      const next = progress.bookmarks.includes(subId)
        ? progress.bookmarks.filter((x) => x !== subId)
        : [...progress.bookmarks, subId];
      persist({ bookmarks: next });
    },
    [progress, persist]
  );

  const markSubtopicComplete = useCallback(
    (subId) => {
      if (!progress) return;
      if (progress.completed_subtopics.includes(subId)) return;
      const next = [...progress.completed_subtopics, subId];
      persist({ completed_subtopics: next });
    },
    [progress, persist]
  );

  const saveQuizResult = useCallback(
    (moduleId, result) => {
      if (!progress) return;
      const prevScores = progress.quiz_scores || {};
      const nextScores = { ...prevScores, [moduleId]: result };
      const passed = result.passed;
      const completedModules = progress.completed_modules.includes(moduleId)
        ? progress.completed_modules
        : passed
        ? [...progress.completed_modules, moduleId]
        : progress.completed_modules;
      persist({ quiz_scores: nextScores, completed_modules: completedModules });
    },
    [progress, persist]
  );

  const markCertificateIssued = useCallback(() => {
    if (!progress || progress.certificate_issued) return;
    persist({ certificate_issued: true });
  }, [progress, persist]);

  // —————— derived ——————
  const derived = useMemo(() => {
    const allSubIds = getAllSubtopicIds();
    const totalModules = COURSE.modules.length;
    const completedSubs = progress ? progress.completed_subtopics.length : 0;
    const completedMods = progress ? progress.completed_modules.length : 0;
    const totalWeight = allSubIds.length + totalModules;
    const doneWeight = completedSubs + completedMods;
    const percent = totalWeight > 0 ? Math.round((doneWeight / totalWeight) * 100) : 0;
    const allModulesPassed = completedMods >= totalModules;
    return { percent, completedMods, totalModules, allModulesPassed };
  }, [progress]);

  const value = {
    user,
    progress,
    loading,
    saving,
    theme,
    setTheme,
    registerUser,
    toggleBookmark,
    markSubtopicComplete,
    saveQuizResult,
    markCertificateIssued,
    ...derived,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
